/**
 * The three faces of "not sent" (CU-869f03vek, not-sent analysis of pass rp-202610280707-forceall-92bfd5): a
 * decision without a write row used to get one word and one tooltip — "the push waits for every task of the search,
 * or for its deadline" — that was true for one of three situations. The server now says which (`write_status`
 * `waiting_tasks` | `pending_resend` | `sibling_sent`, with `not_sent_reason`), and this module puts it in words:
 * the chip's label, its tooltip and the detail's reason line. Plain `not_sent` stays the honest fallback.
 *
 * Pure: no React, no dates library — the caller hands `hhmm(iso)` so the time reads as the page's other times.
 */

export const NOT_SENT_STATES = ['waiting_tasks', 'pending_resend', 'sibling_sent']

/** True for the plain word and its three faces — where one bucket is expected (filters, counters, labels). */
export const isNotSentState = (status) => status === 'not_sent' || NOT_SENT_STATES.includes(status)

const reasonOf = (row) => (row && row.not_sent_reason && typeof row.not_sent_reason === 'object' ? row.not_sent_reason : {})
const tasksOf = (r) => (r.tasks_arrived != null && r.tasks_expected != null ? `${r.tasks_arrived} of ${r.tasks_expected}` : null)
const siteOf = (r) => r.sent_site || null

/** The chip's word: short, the state in two or three words. */
export function notSentChipLabel(row) {
	const status = row?.write_status
	const r = reasonOf(row)
	if (status === 'waiting_tasks') return 'not sent · waiting'
	if (status === 'pending_resend') return 're-send pending'
	if (status === 'sibling_sent') return siteOf(r) ? `sent through ${siteOf(r)}` : 'sent through sibling'
	return 'not sent'
}

/** The chip's tooltip, one line; `null` for a status that is not a "not sent". */
export function notSentTitle(row, hhmm = () => '') {
	const status = row?.write_status
	if (!isNotSentState(status)) return null
	const r = reasonOf(row)
	if (status === 'waiting_tasks') {
		const tasks = tasksOf(r)
		const due = hhmm(r.deadline_at)
		const facts = [tasks ? `${tasks} in` : null, due ? `deadline ${due}` : null].filter(Boolean).join(' · ')
		return `Waiting for the search’s other tasks${facts ? `: ${facts}` : ''}. The push goes out when every task has decided, or when the deadline passes.`
	}
	if (status === 'pending_resend') {
		const due = hhmm(r.deadline_at)
		const drain = r.tick_only_drain === false
			? 'runs with the next repricing tick, or when a late task arrives.'
			: 'runs with the next repricing tick (or Push now on the pass).'
		return ['Re-send pending', due ? `due ${due}` : null, drain].filter(Boolean).join(' · ')
	}
	if (status === 'sibling_sent') return `Sent through ${siteOf(r) || 'another site'} · this row is its sibling.`
	return 'Decided, no send recorded for this row.'
}

/** The detail's reason line under "PMS push": the whole story in two sentences. */
export function notSentDetail(row, hhmm = () => '') {
	const status = row?.write_status
	if (!isNotSentState(status)) return null
	const r = reasonOf(row)
	const ladder = 'This decision is one bracket of a ladder the PMS only takes whole.'
	if (status === 'waiting_tasks') {
		const tasks = tasksOf(r)
		const due = hhmm(r.deadline_at)
		return `${ladder} ${tasks ? `${tasks} tasks of its search have decided` : 'Its search is still collecting tasks'}; it goes out when every task has, or when the deadline${due ? ` (${due})` : ''} passes.`
	}
	if (status === 'pending_resend') {
		const sentAt = hhmm(r.flushed_at)
		const late = r.decided_after_send_s != null ? ` this decision landed ${r.decided_after_send_s} s after the send and re-armed it.` : ' a decision landed after the send and re-armed it.'
		const drain = r.tick_only_drain === false
			? `${tasksOf(r) || 'Not every task'}${tasksOf(r) ? ' tasks are' : ' is'} in: a late task’s own callback can still send it, as can the next repricing tick or Push now.`
			: 'Every task is in, so nothing but the next repricing tick — or Push now on the pass — will send it.'
		return `The search was sent once${sentAt ? ` at ${sentAt}` : ''};${late} ${drain}`
	}
	if (status === 'sibling_sent') {
		const site = siteOf(r) || 'another site'
		const at = hhmm(r.sent_at)
		return `Two sites decided this bracket; the lowest target went out once, through ${site}${at ? ` at ${at}` : ''}, for both. This row is the other site’s decision: nothing of it was sent, and nothing is pending.`
	}
	return `${ladder.slice(0, -1)}, and no send of it is recorded: the search has not been sent, or its send did not cover this row.`
}
